/**
 * profile-app-passcode.js
 */

import React from 'react';
import axios from 'axios';
import { Box, Typography, Grid, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { sleep } from 'utils/misc';
import { AppMessagingContext } from 'contexts/app-messaging/app-messaging-context';
import LabeledSwitch from 'components/labeled-switch';
import FeatureBox from 'components/feature-box';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerContent from 'components/menu-drawer/drawer-content';
import MobileDrawerAppBar from 'components/menu-drawer/mobile/mobile-drawer-app-bar';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';


const PasscodeButton = (props) => {
  const { number, letters, onClick, disabled } = props;

  return (
    <Button 
      variant='contained'
      onClick={() => onClick(number)}
      disabled={disabled}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: 62,
        color: 'sys.onSurface',
        backgroundColor: `sys.surfaceContainerHighest`,
        '&:hover:not(:disabled)': {
          backgroundColor: 'sys.surfaceContainerHighest',
        },
        '& .number': {
          fontSize: 'sizes.5',
          lineHeight: 1.3,
          '&.action': {
            fontSize: 'sizes.3',
          }
        },
        '& .letters': {
          fontSize: 'sizes.1',
          letterSpacing: '1px',
        }
      }}
    >
      <Typography className={`number ${number.length > 1 ? 'action' : ''}`}>{number}</Typography>
      {(number !== 'DEL' && number !== 'CLEAR') &&
      <Typography className='letters'>{letters}</Typography>}
    </Button>
  );
}


export default function ProfileAppPasscode({ onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const { 
    appInfo,
    hasPasscode: appHasPasscode,
    setPasscode: appSetPasscode,
    setPasscodeToken: appSetPasscodeToken,
  } = React.useContext(AppMessagingContext);  
  const [isLoading, setIsLoading] = React.useState(false);
  const [hasPasscode, setHasPasscode] = React.useState(appHasPasscode);
  const [passcodeValue, setPasscodeValue] = React.useState(appHasPasscode ? '****' : '');
  const [dirty, setDirty] = React.useState(false);
  const [canSave, setCanSave] = React.useState(false);


  const onToggleUsePasscode = (val) => {
    setHasPasscode(val);
    setDirty(true);
    
    if (val) {
      setCanSave(false);
    } else {
      setPasscodeValue('');
      setCanSave(true);
    } 
  }


  const onPasscodeInput = (val) => {
    let newPasscodeValue = passcodeValue;
    if (val === 'DEL') {
      newPasscodeValue = newPasscodeValue.slice(0, -1);
    } else if (val === 'CLEAR') {
      newPasscodeValue = '';
    } else if (passcodeValue.length < 4) {
      newPasscodeValue += val;
    }

    setDirty(true);
    setCanSave(newPasscodeValue.length === 4 && newPasscodeValue !== passcodeValue);
    setPasscodeValue(newPasscodeValue);
  }


  const onSave = async () => {
    setIsLoading(true);
    let passcodeToken = '';

    if (passcodeValue) {
      // get updated passcode token
      const response = await axios.get(`/account/refresh-passcode-token`);
      if (!response.isSuccess) {
        return;
      }

      passcodeToken = response.data.passcodeToken;

      // support 5.0.1 or lower
      if (appInfo.appVersion <= '5.0.2') {
        await appSetPasscodeToken(passcodeToken);
        await sleep(300);
      }
    }

    await appSetPasscode(passcodeValue, passcodeToken);
    setDirty(false);
    setCanSave(false);
    setIsLoading(false);
    enqueueSnackbar('Passcode Saved', { variant: 'success' });
  }


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!canSave}
      style={{ marginLeft: 16 }}
      onClick={() => onSave()}
      children='Save'
    />
  );

  
  return (
    <>
      <DrawerContentBuilder
        mobileAppBar={{ content: (
          <MobileDrawerAppBar
            title='Quick Access PassCode'
            saveButton={saveButton}
            onClose={onClose}
          />
        )}}
        children={<>
          {hasPasscode !== undefined &&
            <FeatureBox loading={isLoading}>
              <DrawerContent style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-evenly"
                  >
                    <Typography>Quick Access Passcode</Typography>
                    <LabeledSwitch
                      checked={hasPasscode}
                      onChange={e => onToggleUsePasscode(e.target.checked)}
                    />
                  </Box>

                  {/* passcode input */}
                  {hasPasscode &&
                  <>
                    <Grid container spacing={2} sx={{ '& .MuiGrid-item': { display: 'flex' } }}>
                      <Grid item xs={12}>
                        <Box sx={{
                          margin: '0 auto',
                          mt: 4,
                          py: 1,
                          px: 1.5,
                          width: 160,
                          border: (theme) => `1px solid ${theme.palette.sys.primary}`,
                          borderRadius: '4px',
                          textAlign: 'center',
                          fontSize: 'sizes.6',
                          letterSpacing: '4px',
                        }}>
                          {passcodeValue || <>&nbsp;</>}
                        </Box>
                      </Grid>

                      {/* buttons */}
                      <Grid item xs={4}>
                        <PasscodeButton number={1} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={2} letters={'ABC'} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={3} letters={'DEF'} onClick={onPasscodeInput} />
                      </Grid>

                      <Grid item xs={4}>
                        <PasscodeButton number={4} letters={'GHI'} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={5} letters={'JKL'} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={6} letters={'MNO'} onClick={onPasscodeInput} />
                      </Grid>

                      <Grid item xs={4}>
                        <PasscodeButton number={7} letters={'PQRS'} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={8} letters={'TUV'} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={9} letters={'WXYZ'} onClick={onPasscodeInput} />
                      </Grid>

                      <Grid item xs={4}>
                        <PasscodeButton 
                          number='DEL' 
                          onClick={onPasscodeInput} 
                          disabled={passcodeValue.length === 0 || passcodeValue.includes('*')} 
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton number={0} onClick={onPasscodeInput} />
                      </Grid>
                      <Grid item xs={4}>
                        <PasscodeButton 
                          number='CLEAR' 
                          onClick={onPasscodeInput} 
                          disabled={passcodeValue.length === 0}
                        />
                      </Grid>
                    </Grid>
                  </>}

                </Box>

              </DrawerContent>
            </FeatureBox>}
        </>}
      />

      <ConfirmUnsavedEditor 
        dirty={dirty}
        save={async () => {
          if (!canSave) {
            return Promise.reject({ message: 'Unable to save incomplete passcode' });
          }

          onSave();
          return true;
        }}
      />
    </>
  )
}